import type { JourneyDetailDto, JourneyStageDto, JourneyStageKey, OperationsLensKey } from "@/api/endpoints";
import { fmtDateOnly, fmtETDate } from "@/lib/format";
import { INTAKE_SOURCE_LABEL } from "@/features/operations/operationsLabels";

/** What the lifecycle bar shows under one segment: the state colour, two fact lines, the people. */
export interface StageView {
  key: JourneyStageKey;
  stage: JourneyStageDto;
  /** done | current-<attention> | notStarted | skipped; the attention colours the current segment. */
  tone: "done" | "moving" | "stuck" | "blocked" | "stuckAndBlocked" | "notStarted" | "skipped";
  facts: string[];
  people: { initials: string; label: string; hasTheBall?: boolean }[];
}

export type JourneyAttentionKey = OperationsLensKey | "unknown";

/** The attention of the record carrying the journey: the request's verdict, else the intake's. */
export function journeyAttention(d: JourneyDetailDto): { state: JourneyAttentionKey; reason: string | null } | null {
  if (d.state === "complete" || d.state === "canceled") return null;
  if (d.requestAttention) {
    const state = d.requestAttention.item.attention[0];
    if (state === "stuck" || state === "blocked" || state === "stuckAndBlocked") {
      const item = d.requestAttention.item;
      const parts: string[] = [];
      if (state !== "blocked" && item.untouchedBd != null) parts.push(`untouched for ${item.untouchedBd} bd`);
      if (state !== "stuck" && item.sinceStateBd != null) {
        const w = item.waitingOn;
        parts.push(w?.kind === "task" ? `${w.detail ? `task with ${w.detail}` : "task"} for ${item.sinceStateBd} bd` : `work order ${w?.detail ?? ""} for ${item.sinceStateBd} bd`.replace("  ", " "));
      }
      return { state, reason: parts.length ? parts.join(" · ") : null };
    }
    return { state: "clear", reason: null };
  }
  if (d.intakeAttention) {
    const state = d.intakeAttention.state as JourneyAttentionKey;
    return { state, reason: d.intakeAttention.reason };
  }
  return null;
}

function initialsOf(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
}

function intakeSourceLine(d: JourneyDetailDto): string {
  const i = d.intake!;
  const system = i.sourceSystem?.replace(/^Procom/i, "") ?? null;
  return `${INTAKE_SOURCE_LABEL[i.source]}${system ? ` · ${system}` : ""}${i.sourceReference ? ` ${i.sourceReference}` : ""}`;
}

/** Builds the six bar segments from the journey: colour by status and attention, facts from the records. */
export function stageViews(d: JourneyDetailDto): StageView[] {
  const attention = journeyAttention(d);
  const currentTone: StageView["tone"] = attention && (attention.state === "stuck" || attention.state === "blocked" || attention.state === "stuckAndBlocked") ? attention.state : "moving";
  const req = d.request;
  const openTasks = req ? req.tasks.filter((t) => t.taskCompletedDatetime == null && !t.wasDiscarded).length : 0;
  const doneTasks = req ? req.tasks.length - openTasks : 0;
  const assigned = req?.assignedToUserName ?? null;
  const cs = assigned ? [{ initials: initialsOf(assigned), label: `CS · ${assigned}` }] : [];
  const worker = d.worker ? [{ initials: initialsOf(d.worker), label: "Worker" }] : [];

  return (["intake", "preOnboarding", "awaitingRequest", "requestProcessing", "woSubmit", "woApproval"] as const).map((key) => {
    const stage = d.stages.find((s) => s.key === key) ?? { key, status: "skipped" as const, start: null, end: null, bd: null, cd: null, peerMedianBd: null, peerCount: null, sentBack: false };
    const tone: StageView["tone"] = stage.status === "done" ? "done" : stage.status === "current" ? currentTone : stage.status;
    const facts: string[] = [];
    let people: StageView["people"] = [];
    switch (key) {
      case "intake":
        if (d.intake) {
          facts.push(intakeSourceLine(d));
          facts.push(d.intake.isOpen ? `status ${d.intake.status ?? "unknown"} since ${fmtETDate(d.intake.statusChangedDateTime)}` : d.intake.endedDateTime ? `accepted ${fmtETDate(d.intake.endedDateTime)}` : stage.end ? `handed over ${fmtETDate(stage.end)}` : "");
          if (d.intake.assignedToName) people = [{ initials: initialsOf(d.intake.assignedToName), label: d.intake.assignedToName, hasTheBall: d.carrier === "intake" || d.carrier === "awaitingRequest" }];
        } else {
          facts.push("no intake record");
        }
        break;
      case "preOnboarding":
        if (d.preOnboarding) {
          facts.push(`${d.preOnboarding.requestType} · ${d.preOnboarding.calculatedStatus ?? d.preOnboarding.status}`);
          facts.push(d.workOrder?.createdDateTime ? `WO created ${fmtETDate(d.workOrder.createdDateTime)}` : "no work order yet");
          people = worker;
        } else {
          facts.push("none");
        }
        break;
      case "awaitingRequest":
        if (stage.status !== "skipped") {
          facts.push(d.workOrder ? `WO ${d.workOrder.workOrderNumber ?? d.workOrder.workOrderId} created ${fmtETDate(d.workOrder.createdDateTime)}` : "");
          // The work order is the record that matters here (Rafael, 2026-10-27): its state, who holds it, when it was last edited, the contract.
          if (d.workOrder && stage.status === "current") {
            const wo = d.workOrder;
            const state = wo.approveDateTime ? "approved" : wo.submitDateTime ? (wo.rejectCount > 0 ? "sent back, resubmitted" : "submitted, pending review") : "draft, not submitted";
            facts.push([state, wo.assignedToUserName ? `assigned to ${wo.assignedToUserName}` : null, wo.lastModifiedDateTime ? `last edit ${fmtETDate(wo.lastModifiedDateTime)}` : null].filter(Boolean).join(" · "));
            if (wo.versionStartDate) facts.push(`contract ${fmtDateOnly(wo.versionStartDate)} → ${wo.versionEndDate ? fmtDateOnly(wo.versionEndDate) : "open-ended"}`);
          }
          facts.push(stage.status === "current" ? "no request on the work order yet" : d.request ? `request created ${fmtETDate(d.request.requestCreatedDateTime)}` : "");
          if (stage.status === "current" && d.intake?.assignedToName) people = [{ initials: initialsOf(d.intake.assignedToName), label: `CS · ${d.intake.assignedToName}`, hasTheBall: true }];
        } else {
          facts.push(d.request ? "request raised with its work order" : "no work order");
        }
        break;
      case "requestProcessing":
        if (req) {
          facts.push(`${doneTasks} of ${req.tasks.length} tasks done${req.requestReleasedDateTime ? "" : " · not released"}`);
          facts.push(stage.status === "current" && attention?.reason ? attention.reason : req.requestCompletedDateTime ? `last task done ${fmtETDate(req.requestCompletedDateTime)}` : "");
          people = [...(assigned ? [{ ...cs[0]!, hasTheBall: stage.status === "current" && d.requestAttention?.item.waitingOn?.kind === "task" && d.requestAttention.item.waitingOn.detail === assigned }] : []), ...worker];
        } else {
          facts.push(stage.status === "notStarted" ? "not created" : "");
        }
        break;
      case "woSubmit":
        facts.push(d.workOrder?.submitDateTime ? `submitted ${fmtETDate(d.workOrder.submitDateTime)}` : stage.status === "current" ? "request done, work order not submitted" : "request done → WO submitted");
        if (stage.status !== "skipped") people = cs.map((p) => ({ ...p, hasTheBall: stage.status === "current" }));
        break;
      case "woApproval":
        if (d.workOrder) {
          facts.push(`submitted ${d.workOrder.submitCount}× · sent back ${d.workOrder.rejectCount}×`);
          facts.push(d.workOrder.approveDateTime ? `approved ${fmtETDate(d.workOrder.approveDateTime)}${d.workOrder.approver ? ` by ${d.workOrder.approver}` : ""}` : stage.status === "current" ? `waiting since ${fmtETDate(d.workOrder.lastActionDateTime ?? d.workOrder.submitDateTime)}` : "WO submitted → approved");
          people = d.workOrder.approver ? [{ initials: initialsOf(d.workOrder.approver), label: `Approver · ${d.workOrder.approver}` }] : stage.status === "current" ? [{ initials: "OQ", label: "Operations queue", hasTheBall: true }] : [];
        } else {
          facts.push(stage.status === "skipped" ? "no work order" : "WO submitted → approved");
        }
        break;
    }
    return { key, stage, tone, facts: facts.filter(Boolean), people };
  });
}

/** Flex weight of a segment: elapsed time, with a floor so empty and short segments keep their label. */
export function segmentWeight(view: StageView): number {
  if (view.stage.status === "skipped") return 0.8;
  if (view.stage.status === "notStarted") return 1;
  const bd = view.stage.bd ?? 0;
  return Math.max(view.stage.status === "current" ? 2.4 : 1.1, Math.min(4.2, 0.9 + bd * 0.8));
}

export function ratioLabel(bd: number | null, median: number | null): { text: string; tone: "fast" | "slow" | "critical" | "none" } {
  if (bd == null || median == null || median < 0.05) return { text: "—", tone: "none" };
  const r = bd / median;
  if (r <= 1) return { text: "faster", tone: "fast" };
  return { text: `${r.toFixed(1)}×`, tone: r < 2 ? "slow" : "critical" };
}
